// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import type { RestorableDatabaseAccounts } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { CosmosDBManagementClient } from "../cosmosDBManagementClient.js";
import type {
  RestorableDatabaseAccountGetResult,
  RestorableDatabaseAccountsListByLocationOptionalParams,
  RestorableDatabaseAccountsListByLocationResponse,
  RestorableDatabaseAccountsListOptionalParams,
  RestorableDatabaseAccountsListResponse,
  RestorableDatabaseAccountsGetByLocationOptionalParams,
  RestorableDatabaseAccountsGetByLocationResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing RestorableDatabaseAccounts operations. */
export class RestorableDatabaseAccountsImpl implements RestorableDatabaseAccounts {
  private readonly client: CosmosDBManagementClient;

  /**
   * Initialize a new instance of the class RestorableDatabaseAccounts class.
   * @param client Reference to the service client
   */
  constructor(client: CosmosDBManagementClient) {
    this.client = client;
  }

  /**
   * Lists all the restorable Azure Cosmos DB database accounts available under the subscription and in a
   * region.  This call requires 'Microsoft.DocumentDB/locations/restorableDatabaseAccounts/read'
   * permission.
   * @param location Cosmos DB region, with spaces between words and each word capitalized.
   * @param options The options parameters.
   */
  public listByLocation(
    location: string,
    options?: RestorableDatabaseAccountsListByLocationOptionalParams,
  ): PagedAsyncIterableIterator<RestorableDatabaseAccountGetResult> {
    const iter = this.listByLocationPagingAll(location, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listByLocationPagingPage(location, options, settings);
      },
    };
  }

  private async *listByLocationPagingPage(
    location: string,
    options?: RestorableDatabaseAccountsListByLocationOptionalParams,
    _settings?: PageSettings,
  ): AsyncIterableIterator<RestorableDatabaseAccountGetResult[]> {
    let result: RestorableDatabaseAccountsListByLocationResponse;
    result = await this._listByLocation(location, options);
    yield result.value || [];
  }

  private async *listByLocationPagingAll(
    location: string,
    options?: RestorableDatabaseAccountsListByLocationOptionalParams,
  ): AsyncIterableIterator<RestorableDatabaseAccountGetResult> {
    for await (const page of this.listByLocationPagingPage(location, options)) {
      yield* page;
    }
  }

  /**
   * Lists all the restorable Azure Cosmos DB database accounts available under the subscription. This
   * call requires 'Microsoft.DocumentDB/locations/restorableDatabaseAccounts/read' permission.
   * @param options The options parameters.
   */
  public list(
    options?: RestorableDatabaseAccountsListOptionalParams,
  ): PagedAsyncIterableIterator<RestorableDatabaseAccountGetResult> {
    const iter = this.listPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(options, settings);
      },
    };
  }

  private async *listPagingPage(
    options?: RestorableDatabaseAccountsListOptionalParams,
    _settings?: PageSettings,
  ): AsyncIterableIterator<RestorableDatabaseAccountGetResult[]> {
    let result: RestorableDatabaseAccountsListResponse;
    result = await this._list(options);
    yield result.value || [];
  }

  private async *listPagingAll(
    options?: RestorableDatabaseAccountsListOptionalParams,
  ): AsyncIterableIterator<RestorableDatabaseAccountGetResult> {
    for await (const page of this.listPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Lists all the restorable Azure Cosmos DB database accounts available under the subscription and in a
   * region.  This call requires 'Microsoft.DocumentDB/locations/restorableDatabaseAccounts/read'
   * permission.
   * @param location Cosmos DB region, with spaces between words and each word capitalized.
   * @param options The options parameters.
   */
  private _listByLocation(
    location: string,
    options?: RestorableDatabaseAccountsListByLocationOptionalParams,
  ): Promise<RestorableDatabaseAccountsListByLocationResponse> {
    return this.client.sendOperationRequest({ location, options }, listByLocationOperationSpec);
  }

  /**
   * Lists all the restorable Azure Cosmos DB database accounts available under the subscription. This
   * call requires 'Microsoft.DocumentDB/locations/restorableDatabaseAccounts/read' permission.
   * @param options The options parameters.
   */
  private _list(
    options?: RestorableDatabaseAccountsListOptionalParams,
  ): Promise<RestorableDatabaseAccountsListResponse> {
    return this.client.sendOperationRequest({ options }, listOperationSpec);
  }

  /**
   * Retrieves the properties of an existing Azure Cosmos DB restorable database account.  This call
   * requires 'Microsoft.DocumentDB/locations/restorableDatabaseAccounts/read/*' permission.
   * @param location Cosmos DB region, with spaces between words and each word capitalized.
   * @param instanceId The instanceId GUID of a restorable database account.
   * @param options The options parameters.
   */
  getByLocation(
    location: string,
    instanceId: string,
    options?: RestorableDatabaseAccountsGetByLocationOptionalParams,
  ): Promise<RestorableDatabaseAccountsGetByLocationResponse> {
    return this.client.sendOperationRequest(
      { location, instanceId, options },
      getByLocationOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listByLocationOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.DocumentDB/locations/{location}/restorableDatabaseAccounts",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.RestorableDatabaseAccountsListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId, Parameters.location1],
  headerParameters: [Parameters.accept],
  serializer,
};
const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.DocumentDB/restorableDatabaseAccounts",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.RestorableDatabaseAccountsListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const getByLocationOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.DocumentDB/locations/{location}/restorableDatabaseAccounts/{instanceId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.RestorableDatabaseAccountGetResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.location1,
    Parameters.instanceId,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
